import { z } from "zod";

import { LIMITS, PAGINATION, SORT_ORDERS } from "../constants.js";
import { isEmailAddress } from "../address.js";
import type { EntityId, Ulid } from "../ids.js";
import { isUlid } from "../ids.js";
import { isIsoUtc } from "../time.js";

/** Schema for a canonical ULID string. */
export const ulidSchema: z.ZodType<Ulid> = z.custom<Ulid>(
  (value) => typeof value === "string" && isUlid(value),
  "Invalid ULID",
);

/** Builds a branded entity-id schema from the ULID schema. */
export function entityIdSchema<Kind extends string>(kind: Kind): z.ZodType<EntityId<Kind>> {
  void kind;
  return z.custom<EntityId<Kind>>(
    (value) => typeof value === "string" && isUlid(value),
    "Invalid entity id",
  );
}

/** Alias kept for readability at call sites. */
export const idSchema = ulidSchema;

/** UTC ISO-8601 timestamp with millisecond precision. */
export const isoUtcSchema = z
  .string()
  .refine((value) => isIsoUtc(value), "Must be a UTC ISO-8601 timestamp");

/** A routable email address. */
export const emailAddressSchema = z
  .string()
  .min(3)
  .max(LIMITS.maxAddressLength)
  .refine((value) => isEmailAddress(value), "Must be a valid email address");

/** `{ name?, address }` pair used in every address-bearing payload. */
export const addressObjectSchema = z.object({
  name: z.string().max(LIMITS.maxDisplayNameLength).optional(),
  address: emailAddressSchema,
});
export type AddressObject = z.infer<typeof addressObjectSchema>;

/** JSON object with unknown leaves, for audit and log detail. */
export const jsonObjectSchema = z.record(z.string(), z.unknown());

/** Query-string boolean: only the literals "true" / "false". */
export const booleanQuerySchema = z.enum(["true", "false"]).transform((value) => value === "true");

/** Opaque keyset cursor. */
export const cursorSchema = z.string().min(1).max(512);

/** Standard list controls shared by every collection endpoint. */
export const listQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(PAGINATION.maxLimit).default(PAGINATION.defaultLimit),
  cursor: cursorSchema.optional(),
  sortOrder: z.enum([...SORT_ORDERS]).default("desc"),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

/** Pagination metadata returned alongside every collection. */
export const pageInfoSchema = z.object({
  limit: z.number().int().positive(),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});
export type PageInfo = z.infer<typeof pageInfoSchema>;

/** One field-level validation failure. */
export const errorDetailSchema = z.object({
  field: z.string(),
  code: z.string(),
  message: z.string(),
});
export type ErrorDetail = z.infer<typeof errorDetailSchema>;

/** Uniform error envelope for every non-2xx JSON response. */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    retryable: z.boolean(),
    request_id: z.string(),
    details: z.array(errorDetailSchema).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

/** Uniform acknowledgement for DELETE and other empty-body mutations. */
export const ackSchema = z.object({
  ok: z.literal(true),
  id: ulidSchema.optional(),
});
export type Ack = z.infer<typeof ackSchema>;

/* -------------------------------------------------------------------------- */
/* Entity id schemas                                                           */
/* -------------------------------------------------------------------------- */

export const userIdSchema = entityIdSchema("UserId");
export const sessionIdSchema = entityIdSchema("SessionId");
export const passkeyIdSchema = entityIdSchema("PasskeyId");
export const domainIdSchema = entityIdSchema("DomainId");
export const mailboxIdSchema = entityIdSchema("MailboxId");
export const aliasIdSchema = entityIdSchema("AliasId");
export const messageIdSchema = entityIdSchema("MessageId");
export const threadIdSchema = entityIdSchema("ThreadId");
export const labelIdSchema = entityIdSchema("LabelId");
export const attachmentIdSchema = entityIdSchema("AttachmentId");
export const draftIdSchema = entityIdSchema("DraftId");
export const contactIdSchema = entityIdSchema("ContactId");
export const routingRuleIdSchema = entityIdSchema("RoutingRuleId");
export const auditLogIdSchema = entityIdSchema("AuditLogId");
export const bounceIdSchema = entityIdSchema("BounceId");
export const spamScoreIdSchema = entityIdSchema("SpamScoreId");
export const deliveryIdSchema = entityIdSchema("DeliveryId");
export const exportJobIdSchema = entityIdSchema("ExportJobId");
